import { PrismaEnums } from '@airreading/schema/browser';
import { z } from 'zod';

import { CoachGrade, coachGradeSchema } from '../coach-grades.js';
import { filteringSchemaFrom, pagingSchema, sortingSchemaFrom, sortOrderSchemaFrom } from './modifier.js';
import { paginatedRequest, paginatedResponse } from './util-schema-types.js';

/**
 * This single import eventually forms an import cycle with `users.js`.
 */
import { Temporal } from 'temporal-polyfill';
import {
  ClassEligibility,
  classEligibilitySchema,
  coachAvailabilityFromFilterSchema,
  coachAvailabilitySchema,
  coachOverallGradeEnum,
} from './coach-availabilities.js';
import { backgroundCheckStatusSchema, teachingPreferenceSchema, techCheckTypeSchema } from './coach-shared.js';
import { dayOfWeekSchema } from './meeting-block.js';
import { OrganizationMatchNames } from './org.js';
import { plainSessionLikeSchema } from './session.js';
import { studentGroupWithClassTimeSchema } from './student-group-shared.js';
import { paymentAccountType } from './index.js';
import { logicalOperatorSchema } from '../query-utils.js';

export const zoomTypeSchema = z.nativeEnum(PrismaEnums.ZoomType);
export type ZoomType = z.infer<typeof zoomTypeSchema>;
export const ZoomType = zoomTypeSchema.enum;

export const ZoomTypeLabelMap: Record<ZoomType, string> = {
  [ZoomType.basic]: 'Basic',
  [ZoomType.license]: 'Licensed',
};

// Specific ordering of Zoom types for UI purposes, "-" represents no license selected (null)
export const zoomNoLicenseString = '-';
export type ZoomNoLicenseString = typeof zoomNoLicenseString;
export const ZoomLicenseOptions: (ZoomType | ZoomNoLicenseString)[] = [
  ZoomType.license,
  ZoomType.basic,
  zoomNoLicenseString,
];

export const zoomInviteStatusSchema = z.nativeEnum(PrismaEnums.ZoomInviteStatus);
export type ZoomInviteStatus = z.infer<typeof zoomInviteStatusSchema>;
export const ZoomInviteStatus = zoomInviteStatusSchema.enum;

/**
 * Although Prisma exposes CoachStatus via $Enums, it is not reliably available
 * in all environments, so it is defined manually here to ensure access.
 */
export const coachStatusSchema = z.enum(['onboarding', 'ready_to_teach', 'offboarded', 'pause']);
export type CoachStatus = z.infer<typeof coachStatusSchema>;
export const CoachStatus = coachStatusSchema.enum;
export const CoachStatusLabelMap: Record<CoachStatus, string> = {
  [CoachStatus.onboarding]: 'Onboarding',
  [CoachStatus.ready_to_teach]: 'Ready to Teach',
  [CoachStatus.offboarded]: 'Offboarded',
  [CoachStatus.pause]: 'Pause',
};

export const CoachStatusColorMap: Record<CoachStatus, string> = {
  [CoachStatus.onboarding]: '#2563EB',
  [CoachStatus.ready_to_teach]: '#22C55E',
  [CoachStatus.offboarded]: '#8E8E93',
  [CoachStatus.pause]: '#EA580C',
};

export const coachSubStatusSchema = z.enum([
  'offboard_requested',
  'teacher_requested',
  'teaching_quality_issues',
  'tech_issues',
  'absenteeism',
  'non_renewal_of_contract',
  'c_background',
  'n_background',
  'checkr_issue',
]);
export type CoachSubStatus = z.infer<typeof coachSubStatusSchema>;
export const CoachSubStatus = coachSubStatusSchema.enum;
export const CoachSubStatusLabelMap: Record<CoachSubStatus, string> = {
  [CoachSubStatus.offboard_requested]: 'Offboard Requested',
  [CoachSubStatus.teacher_requested]: 'Teacher Requested',
  [CoachSubStatus.teaching_quality_issues]: 'Teaching Quality Issues',
  [CoachSubStatus.tech_issues]: 'Tech Issues',
  [CoachSubStatus.absenteeism]: 'Absenteeism',
  [CoachSubStatus.non_renewal_of_contract]: 'Non-Renewal of Contract',
  [CoachSubStatus.c_background]: 'C Background',
  [CoachSubStatus.n_background]: 'N Background',
  [CoachSubStatus.checkr_issue]: 'Checkr Issue',
};

export const CoachStatusTree: Record<CoachStatus, CoachSubStatus[]> = {
  [CoachStatus.onboarding]: [],
  [CoachStatus.ready_to_teach]: [],
  [CoachStatus.offboarded]: [],
  [CoachStatus.pause]: [
    CoachSubStatus.offboard_requested,
    CoachSubStatus.teacher_requested,
    CoachSubStatus.teaching_quality_issues,
    CoachSubStatus.tech_issues,
    CoachSubStatus.absenteeism,
    CoachSubStatus.non_renewal_of_contract,
    CoachSubStatus.c_background,
    CoachSubStatus.n_background,
    CoachSubStatus.checkr_issue,
  ],
};

export type BackgroundCheckStatus = z.infer<typeof backgroundCheckStatusSchema>;
export const BackgroundCheckStatus = backgroundCheckStatusSchema.enum;
export const BackgroundCheckStatusLabelMap: Record<BackgroundCheckStatus, string> = {
  [BackgroundCheckStatus.checkr_passed]: 'Checkr passed',
  [BackgroundCheckStatus.ebr_fingerprint_passed]: 'EBR fingerprint passed',
  [BackgroundCheckStatus.ebr_fingerprint_quality_reject]: 'EBR fingerprint quality rejection',
  [BackgroundCheckStatus.california_fingerprint_passed]: 'California fingerprint passed',
  [BackgroundCheckStatus.florida_fingerprint_passed]: 'Florida fingerprint passed',
  [BackgroundCheckStatus.florida_foundations_course_completed]: 'Florida Foundations Course completed',
  [BackgroundCheckStatus.uva_cleared]: 'UVA cleared',
  [BackgroundCheckStatus.louisiana_can]: 'Louisiana CAN',
};

export const ClassEligibilityLabelMap: Record<ClassEligibility, string> = {
  [ClassEligibility.can_teach_intervention]: 'Can teach intervention',
  [ClassEligibility.can_teach_practice]: 'Can teach practice',
  [ClassEligibility.can_teach_diagnostic]: 'Can teach diagnostic',
  [ClassEligibility.air_crew]: 'Air Crew',
  [ClassEligibility.imported_from_v1]: 'Imported',
};

export const coachAttendanceGradeSchema = z.enum(['A', 'B', 'C', 'D', 'S', 'Never_Taught']);
export type CoachAttendanceGrade = z.infer<typeof coachAttendanceGradeSchema>;
export const CoachAttendanceGrade = coachAttendanceGradeSchema.enum;
export const CoachAttendanceGradeLabelMap: Record<CoachAttendanceGrade, string> = {
  [CoachAttendanceGrade.A]: 'A',
  [CoachAttendanceGrade.B]: 'B',
  [CoachAttendanceGrade.C]: 'C',
  [CoachAttendanceGrade.D]: 'D',
  [CoachAttendanceGrade.S]: 'S',
  [CoachAttendanceGrade.Never_Taught]: 'Never Taught',
};
export const coachAttendanceGradeOptions = coachAttendanceGradeSchema.options;

export type CoachAttendanceGradeSelectable = CoachAttendanceGrade | 'null';

export function coachAttendanceGradeOption(value: CoachAttendanceGradeSelectable | null | undefined): string {
  if (value === 'null' || value == null) return '(empty)';
  return CoachAttendanceGradeLabelMap[value] ?? '(unknown)';
}

export const teachingGradeEnum = z.enum(['S', 'A', 'B', 'C', 'D', 'New']);
export const teachingGradeOptions = teachingGradeEnum.options;
export const TeachingGrade = teachingGradeEnum.enum;
export const teachingGradeSchema = teachingGradeEnum.nullable();
export type TeachingGrade = z.infer<typeof teachingGradeSchema>;

export const coachOverallGradeOptions = coachOverallGradeEnum.options;
export const CoachOverallGrade = coachOverallGradeEnum.enum;
export const coachOverallGradeSchema = coachOverallGradeEnum.nullable();
export type CoachOverallGrade = z.infer<typeof coachOverallGradeSchema>;
export const CoachOverallGradeLabelMap: Record<NonNullable<CoachOverallGrade>, string> = {
  [CoachOverallGrade.A]: 'A',
  [CoachOverallGrade.B]: 'B',
  [CoachOverallGrade.C]: 'C',
  [CoachOverallGrade.D]: 'D',
  [CoachOverallGrade.S]: 'S',
  [CoachOverallGrade.Never_Taught]: 'Never Taught',
  [CoachOverallGrade.Not_reviewed]: 'Not reviewed',
};
export const CoachOverallGradeColorMap: Record<NonNullable<CoachOverallGrade>, string> = {
  [CoachOverallGrade.A]: '#FF383C',
  [CoachOverallGrade.B]: '#F97316',
  [CoachOverallGrade.C]: '#FB923C',
  [CoachOverallGrade.D]: '#EAB308',
  [CoachOverallGrade.S]: '#DC2626',
  [CoachOverallGrade.Never_Taught]: '',
  [CoachOverallGrade.Not_reviewed]: '',
};

const coachOverallGradeOrder: Record<NonNullable<CoachOverallGrade> | 'null', number> = {
  S: 5,
  A: 4,
  B: 3,
  C: 2,
  D: 1,
  Never_Taught: 0,
  Not_reviewed: 0,
  null: 0,
};

export function coachOverallGradeFrom(input: CoachGrade | CoachAttendanceGrade | null): CoachOverallGrade {
  try {
    return coachOverallGradeSchema.parse(input);
  } catch {
    return null;
  }
}

type LetterGrade = 'D' | 'C' | 'B' | 'A' | 'S';
function minLetterGrade(a: LetterGrade | null, b: LetterGrade | null): LetterGrade | null {
  const aRank = coachOverallGradeOrder[a === null ? 'null' : a];
  const bRank = coachOverallGradeOrder[b === null ? 'null' : b];
  return aRank <= bRank ? a : b;
}

export function calculateOverallGrade(
  quality: CoachGrade | null,
  attendance: CoachAttendanceGrade | null,
): CoachOverallGrade {
  // Max score special case. This is the only way to get an overall grade of 'S'.
  if (quality === 'S' && attendance === 'A') {
    return 'S';
  }

  // When the attendance grade is "Never Taught", the overall grade also becomes
  // "Never Taught" unless the quality grade is at "B" or lower, in which case
  // the overall grade is equal to the quality grade.
  if (attendance === 'Never_Taught') {
    return quality === 'A' || quality === 'S' || quality === 'New' ? 'Never_Taught' : quality;
  }

  // When the quality grade is "New", the overall grade should be "Not_reviewed"
  // for any attendance grade other than "Never_Taught".
  if (quality === 'New') {
    return 'Not_reviewed';
  }

  // In all remaining scenarios, the lower grade prevails.
  return minLetterGrade(quality, attendance);
}

export const coachSchema = z.object({
  id: z.string(),
  zoomPersonalMeetingUrl: z.string().nullable(),
  zoomAccountId: z.string().nullable(),
  zoomType: zoomTypeSchema.nullable(),
  zoomInviteStatus: zoomInviteStatusSchema,
  zoomPersonalMeetingId: z.string().nullable(),
  isTrial: z.boolean().nullable(),
  qualityGrade: coachGradeSchema.nullable(),
  demoGrade: coachGradeSchema.nullable(),
  behaviorNotes: z.string().nullable(),
  smsReminderOptedIn: z.boolean(),
  displayName: z.string().nullable(),
  secondaryEmail: z.string().nullable(),
  needsReview: z.boolean().nullable(),
  status: coachStatusSchema.nullable(),
  subStatuses: z.array(coachSubStatusSchema).nullable(),
  JPSGoogleMeetUrl: z.string().nullable(),
  airReadingGoogleMeetUrl: z.string().nullable(),
  zoomTechCheck: z.boolean().nullable(),
  googleMeetTechCheck: z.boolean().nullable(),
  backgroundCheckStatuses: z.array(backgroundCheckStatusSchema).nullable(),
  classEligibilities: z.array(classEligibilitySchema).nullable(),
  attendanceGrade: coachAttendanceGradeSchema.nullable(),
  overallGrade: coachOverallGradeSchema.nullable(),
  vendorId: z.string().nullable(),
  paymentInstrumentId: z.string().nullable(),
  paymentAccountType: z.lazy(() => paymentAccountType.nullable()),
  floridaTutorId: z.string().optional().nullable(),
  teachingPreferences: z.array(teachingPreferenceSchema).nullable(),
  documentsReviewPassed: z.boolean(),
  workshopPassed: z.boolean(),
  onboardingTechCheckPassed: z.boolean(),
});
export type Coach = z.infer<typeof coachSchema>;

export const coachUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  fullNameOrEmail: z.string(),
  timeZone: z.string().nullable(),
  phone: z.string().nullable(),
  overallGrade: coachOverallGradeSchema.nullable(),
  vendorId: z.string().nullable(),
  paymentAccountType: z.lazy(() => paymentAccountType.nullable()),
  paymentInstrumentId: z.string().nullable(),
});
export type CoachUser = z.infer<typeof coachUserSchema>;

export const coachUserLikeSchema = z.object({
  id: z.string(),
  fullNameOrEmail: z.string(),
  qualityGrade: coachGradeSchema.nullable(),
  attendanceGrade: coachAttendanceGradeSchema.nullable(),
  status: coachStatusSchema.nullable(),
  subStatuses: z.array(coachSubStatusSchema).nullable(),
  zoomTechCheck: z.boolean().nullable(),
  googleMeetTechCheck: z.boolean().nullable(),
  backgroundCheckStatuses: z.array(backgroundCheckStatusSchema).nullable(),
  teachingPreferences: z.array(teachingPreferenceSchema).nullable(),
  classEligibilities: z.array(classEligibilitySchema).nullable(),
  overallGrade: coachOverallGradeSchema.nullable(),
});
export type CoachUserLike = z.infer<typeof coachUserLikeSchema>;

export const fullCoachSchema = coachUserSchema.merge(coachSchema).extend({
  coachName: z.string(),
  studentGroupCount: z.number().nullable(),
  tnsCountPast15Days: z.number().nullable(),
  subRequestCountPast15Days: z.number().nullable(),
  isEdgeCoach: z.boolean(),
});
export type FullCoach = z.infer<typeof fullCoachSchema>;

export const eligibleCoachForClassTimeSchema = z.object({
  id: z.string(),
  fullNameOrEmail: z.string(),
  overallGrade: coachOverallGradeSchema.nullable(),
  conflictType: z.string().nullable(),
  conflictAmount: z.number().nullable(),
  sessions: z.array(plainSessionLikeSchema).optional(),
  substituteCoachSessions: z.array(plainSessionLikeSchema).optional(),
  studentGroupId: z.string().optional(),
});
export type EligibleCoachForClassTime = z.infer<typeof eligibleCoachForClassTimeSchema>;

export const coachWithStudentGroupsSchema = coachSchema.extend({
  studentGroups: z.array(studentGroupWithClassTimeSchema),
});
export type CoachWithStudentGroups = z.infer<typeof coachWithStudentGroupsSchema>;

export const updateCoachOptionsSchema = z.object({
  zoomPersonalMeetingUrl: z.string().nullable().optional(),
  zoomAccountId: z.string().optional(),
  isTrial: z.boolean().optional(),
  qualityGrade: coachGradeSchema.optional(),
  demoGrade: coachGradeSchema.optional(),
  attendanceGrade: coachAttendanceGradeSchema.optional(),
  behaviorNotes: z.string().optional(),
  zoomType: zoomTypeSchema.nullable().optional(),
  zoomPersonalMeetingId: z.string().nullable().optional(),
  zoomInviteStatus: zoomInviteStatusSchema.optional(),
  smsReminderOptedIn: z.boolean().optional(),
  displayName: z.string().optional(),
  secondaryEmail: z.string().nullable().optional(),
  needsReview: z.boolean().optional(),
  JPSGoogleMeetUrl: z.string().nullable().optional(),
  airReadingGoogleMeetUrl: z.string().nullable().optional(),
  zoomTechCheck: z.boolean().optional(),
  googleMeetTechCheck: z.boolean().optional(),
  backgroundCheckStatuses: z.array(backgroundCheckStatusSchema).optional(),
  classEligibilities: z.array(classEligibilitySchema).optional(),
  status: coachStatusSchema.optional(),
  subStatuses: z.array(coachSubStatusSchema).optional(),
  vendorId: z.string().nullable().optional(),
  paymentInstrumentId: z.string().nullable().optional(),
  paymentAccountType: z.lazy(() => paymentAccountType.nullable().optional()),
  floridaTutorId: z.string().optional().nullable(),
  documentsReviewPassed: z.boolean().optional(),
  documentsLastUploadedAt: z.coerce.date().nullable().optional(),
  workshopPassed: z.boolean().optional(),
  onboardingTechCheckPassed: z.boolean().optional(),
});
export type UpdateCoachOptions = z.infer<typeof updateCoachOptionsSchema>;

export const recalculateAttendanceRequestSchema = z.object({
  coachId: z.string().optional(),
});
export type RecalculateAttendanceRequest = z.infer<typeof recalculateAttendanceRequestSchema>;

export const getCoachesSortableColumnSchema = fullCoachSchema
  .keyof()
  .extract([
    'email',
    'fullNameOrEmail',
    'qualityGrade',
    'attendanceGrade',
    'status',
    'needsReview',
    'overallGrade',
    'studentGroupCount',
  ]);
export type GetCoachesSortableColumn = z.infer<typeof getCoachesSortableColumnSchema>;

export const getCoachSortingSchema = sortingSchemaFrom(getCoachesSortableColumnSchema);

export const getCoachesSortOrderSchema = sortOrderSchemaFrom(getCoachesSortableColumnSchema);
export type GetCoachesSortOrder = z.infer<typeof getCoachesSortOrderSchema>;

export const getCoachesFilterSchema = z.object({
  query: z.string().optional(),
  overallGrades: z.array(coachOverallGradeSchema).optional(),
  teachingGrades: z.array(coachGradeSchema).optional(),
  attendanceGrades: z.array(coachAttendanceGradeSchema).optional(),
  statuses: z.array(coachStatusSchema).optional(),
  subStatuses: z.array(coachSubStatusSchema).optional(),
  availabilities: coachAvailabilityFromFilterSchema.optional(),
  classEligibilities: z.array(classEligibilitySchema).optional(),
});
export type GetCoachesFilter = z.infer<typeof getCoachesFilterSchema>;

export const getCoachesModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getCoachesFilterSchema))
  .merge(getCoachSortingSchema);
export type GetCoachesModifier = z.infer<typeof getCoachesModifierSchema>;

export const getSubSessionTeachersModifierSchema = z.object({
  studentGroupId: z.string(),
  startDate: z.string().transform((s) => Temporal.PlainDate.from(s)),
  endDate: z.string().transform((s) => Temporal.PlainDate.from(s)),
});
export type GetSubSessionTeachersModifier = z.infer<typeof getSubSessionTeachersModifierSchema>;

export const getSubSessionTeachersResultsSchema = z.object({
  coaches: z.array(
    eligibleCoachForClassTimeSchema.extend({
      teachingPreferences: z.array(teachingPreferenceSchema),
      qualityGrade: coachGradeSchema.nullable(),
      attendanceGrade: coachAttendanceGradeSchema.nullable(),
      backgroundCheckStatuses: z.array(backgroundCheckStatusSchema),
      weeklySubsCount: z.number().nullable(),
    }),
  ),
});
export type GetSubSessionTeachersResults = z.infer<typeof getSubSessionTeachersResultsSchema>;

export const getCoachesResultsSchema = paginatedResponse.extend({
  coaches: z.array(fullCoachSchema),
});
export type GetCoachesResults = z.infer<typeof getCoachesResultsSchema>;

export const timeRangeSchema = z.object({
  day: dayOfWeekSchema,
  start: z.string(),
  end: z.string(),
});
export type TimeRange = z.infer<typeof timeRangeSchema>;

export const getCoachesAvailabilitiesQuerySchema = z.object({
  query: z.string().optional(),
  statuses: z.array(coachStatusSchema).optional(),
  overallGrades: z.array(coachOverallGradeSchema).optional(),
  classEligibilities: z.array(classEligibilitySchema).optional(),
  teachingDays: z.array(dayOfWeekSchema).optional(),
  backgroundCheckStatuses: z.array(backgroundCheckStatusSchema).optional(),
  backgroundCheckStatusesLogicalOperator: logicalOperatorSchema.optional(),
  googleMeetTechCheck: z.boolean().optional(),
  timeRanges: z.array(timeRangeSchema).optional(),
  untakenTeachersOnly: z.boolean().optional(),
  teachingPreferences: z.array(z.enum(['regular_only', 'sub_only', 'both'])).optional(),
});
export type GetCoachesAvailabilitiesQuery = z.infer<typeof getCoachesAvailabilitiesQuerySchema>;

export const getCoachesAvailabilitiesPaginatedQuerySchema = paginatedRequest.extend(
  getCoachesAvailabilitiesQuerySchema.shape,
);
export type GetCoachesAvailabilitiesPaginatedQuery = z.infer<typeof getCoachesAvailabilitiesPaginatedQuerySchema>;

export const getCoachesAvailabilitiesModifierSchema = pagingSchema.merge(
  filteringSchemaFrom(getCoachesAvailabilitiesQuerySchema),
);
export type GetCoachesAvailabilitiesModifier = z.infer<typeof getCoachesAvailabilitiesModifierSchema>;

export const coachAvailabilityItemSchema = z.object({
  coach: coachUserLikeSchema,
  availabilities: z.array(coachAvailabilitySchema),
  scheduledSessions: z.array(plainSessionLikeSchema),
  scheduledClasses: z.array(plainSessionLikeSchema),
});
export type CoachAvailabilityItem = z.infer<typeof coachAvailabilityItemSchema>;

export const scheduledClassItemSchema = z.object({
  classTimes: z.string(),
  startPlainDate: z.instanceof(Temporal.PlainDate),
  endPlainDate: z.instanceof(Temporal.PlainDate),
  sessionsLeft: z.number(),
  studentGroupId: z.string(),
  schoolId: z.string(),
});
export type ScheduledClassItem = z.infer<typeof scheduledClassItemSchema>;

export const getCoachesAvailabilitiesResultsSchema = paginatedResponse.extend({
  coachAvailabilities: z.array(coachAvailabilityItemSchema),
});
export type GetCoachesAvailabilitiesResults = z.infer<typeof getCoachesAvailabilitiesResultsSchema>;

export const getCoachGoogleMeetUrlForOrg = (
  coach: {
    JPSGoogleMeetUrl: string | null;
    airReadingGoogleMeetUrl: string | null;
  },
  orgName: string,
) => {
  if (orgName === OrganizationMatchNames.JPS) {
    return coach.JPSGoogleMeetUrl;
  }
  return coach.airReadingGoogleMeetUrl;
};

export const coachMissingZoomLicense = fullCoachSchema.extend({
  sessions: z.array(
    z.object({
      startInstant: z.instanceof(Temporal.Instant),
      endInstant: z.instanceof(Temporal.Instant),
    }),
  ),
});

export type CoachMissingZoomLicense = z.infer<typeof coachMissingZoomLicense>;

export const getCoachesMissingZoomLicenseSchema = paginatedResponse.extend({
  coaches: z.array(coachMissingZoomLicense),
});

export type GetCoachesMissingZoomLicenseResult = z.infer<typeof getCoachesMissingZoomLicenseSchema>;

export const coachMissingGoogleMeetSchema = z.object({
  id: z.string(),
  fullNameOrEmail: z.string(),
  email: z.string(),
  nextGoogleMeetSession: z.object({
    id: z.string(),
    startInstant: z.instanceof(Temporal.Instant),
    schoolName: z.string(),
    districtName: z.string(),
  }),
});
export type CoachMissingGoogleMeet = z.infer<typeof coachMissingGoogleMeetSchema>;

export const getCoachesMissingGoogleMeetSortableColumnSchema = z.enum(['fullNameOrEmail', 'nextGoogleMeetSessionDate']);
export type GetCoachesMissingGoogleMeetSortableColumn = z.infer<typeof getCoachesMissingGoogleMeetSortableColumnSchema>;

export const getCoachesMissingGoogleMeetSortOrderSchema = sortOrderSchemaFrom(
  getCoachesMissingGoogleMeetSortableColumnSchema,
);

const getCoachesMissingGoogleMeetSortingSchema = sortingSchemaFrom(getCoachesMissingGoogleMeetSortableColumnSchema);

const getCoachesMissingGoogleMeetFilterSchema = z.object({
  query: z.string().optional(),
});

export const getCoachesMissingGoogleMeetModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getCoachesMissingGoogleMeetFilterSchema))
  .merge(getCoachesMissingGoogleMeetSortingSchema);
export type GetCoachesMissingGoogleMeetModifier = z.infer<typeof getCoachesMissingGoogleMeetModifierSchema>;

export const getCoachesMissingGoogleMeetSchema = paginatedResponse.extend({
  coaches: z.array(coachMissingGoogleMeetSchema),
});
export type GetCoachesMissingGoogleMeetResult = z.infer<typeof getCoachesMissingGoogleMeetSchema>;

export type TechCheckType = z.infer<typeof techCheckTypeSchema>;
export const TechCheckType = techCheckTypeSchema.enum;
export const TechCheckTypeLabelMap: Record<TechCheckType, string> = {
  [TechCheckType.zoomTechCheck]: 'Zoom tech check',
  [TechCheckType.googleMeetTechCheck]: 'Google Meet tech check',
};
